<script setup lang="ts">
import { ref, computed } from '#imports';
import { useResizeObserver } from '@vueuse/core';

import { UiStyles, UiSize } from '../../styles/Styles';
import { UiBackgroundAbstract } from '../backgroundAbstract';
import { UiIconButton, UiIconName } from '../icon';
import { useOpen } from '../../utils/useOpen';

const props = withDefaults(defineProps<{
  size?: UiSize;
  full?: boolean;
  render?: boolean,
  showClose?: boolean,
  outsideClose?: boolean,
  footerLine?: boolean,
  padding?: boolean,
  disabled?: boolean,
  heading?: string,
  subheading?: string,
}>(), {
  size: UiSize.Large,
  full: true,
  render: true,
  showClose: true,
  outsideClose: true,
  footerLine: true,
  padding: true,
  disabled: false,
  heading: '',
  subheading: '',
});

const modalContentRef = ref();
const showHeaderLine = ref(false);

useResizeObserver(modalContentRef, (entries) => {
  const target = entries[0].target;
  showHeaderLine.value = target.scrollHeight > target.clientHeight;
});

const modelValue = defineModel<boolean>({ default: false });
const emits = defineEmits([]);
const { isOpen, open, close } = useOpen(modelValue, emits);

const slots = defineSlots<{
  opener?(): void;
  footer?(): void;
  header?(): void;
  headerIcon?(): void;
}>();
const hasFooter = computed(() => Boolean(slots.footer));

const classView = computed(() => {
  const result = [`_${props.size}`];

  if (!props.full) {
    result.push('_not-full');
  }

  if (props.padding) {
    result.push('_padding');
  }

  return result;
});

function onOpen() {
  if (!props.disabled) {
    open();
  }
}
</script>

<template lang="pug">
.ui-modal(:class="classView")
  slot(name="opener" :onOpen="onOpen" :open="onOpen" :disabled="disabled")
  UiBackgroundAbstract.modal-background(
    :visible="isOpen"
    :render="render"
    :clickable="outsideClose"
    @click:end="close"
    @click:external="close"
  )
    .modal-window
      .modal-header.flex-between-center.row-8.br-x-8(v-if="slots.header || slots.headerIcon || heading || subheading || showClose" data-qa="header" :class="{'_header-line': showHeaderLine}" )
        slot(name="header")
          .header-icon(v-if="slots.headerIcon")
            slot(name="headerIcon")
          .heading
            span(v-if="heading" data-qa="heading") {{ $t(heading) }}
            .subheading(v-if="subheading" data-qa="subheading") {{ $t(subheading) }}
        UiIconButton.close(
          v-if="showClose"
          :size="0"
          :padding="false"
          :theme="UiStyles.UiTheme.Secondary"
          :name="UiIconName.UserInterface_CloseThin"
          @click="close"
        )
      .modal-content(ref="modalContentRef" data-qa="modal-content")
        slot(:onClose="close" :close="close" :isOpen="isOpen")
      .modal-action.flex.row-8(v-if="hasFooter" data-qa="modal-actions" :class="{'_footer-line': footerLine}")
        slot(name="footer" :onClose="close" :close="close")
</template>

<style lang="scss" scoped>
@import '../../styles/colors';
@import '../../styles/typography';
@import '../../styles/adaptive';
@import '../../styles/footer';

.modal-window {
  display: flex;
  flex-flow: column;
  background: $ui-color-bg-default;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.16);
  width: calc(100vw - 48px);
  min-width: min(440px, 100vw - 48px);
  max-width: 1280px;
  max-height: calc(100vh - 40px);
  --uno: br-8;
}

.ui-modal {
  &._small {
    .modal-window {
      max-width: min(440px, 100vw - 48px);
    }
  }

  &._medium {
    .modal-window {
      max-width: min(620px, 100vw - 48px);
    }
  }

  &._big {
    .modal-window {
      max-width: min(920px, 100vw - 48px);
    }
  }

  &._padding {
    .modal-content {
      padding: 16px 24px;
    }
  }

  &._not-full {
    .modal-window {
      width: max-content;
      min-width: 440px;
    }
  }
}

.modal-header {
  @include ui-typography-XL-2;
  padding: 20px 24px 16px;
  color: $ui-color-text-default;
  min-height: 64px;
  max-height: 88px;

  &._header-line {
    box-shadow: inset 0px -1px 0px $ui-color-border-default;
  }

  .header-icon {
    margin-top: 2px;
  }

  .subheading {
    @include ui-typography-M-1;
  }
}

:deep(.close) {
  --uno: s-24;
  color: $ui-color-icon-default;
  margin-left: auto;
}

.modal-content {
  display: flex;
  overflow: hidden;
}

.modal-action {
  justify-content: flex-end;
  padding: 16px 24px;

  &._footer-line {
    box-shadow: inset 0 1px 0 $ui-color-border-default;
  }
}
</style>
